import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { EditIcon, GripVertical } from "lucide-react";
import { useState } from "react";
import { Card as BaseCard } from "@stackje/shared-components/ui/card";
import type { Card as CardType } from "@stackje/common/config";
import { Link, useNavigate } from "@tanstack/react-router";
import clsx from "clsx";

function SortableCard({
  card,
  index,
  isPendingChanges,
}: {
  card: CardType;
  index: number;
  isPendingChanges: boolean;
}) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({ id: index });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : "translate3d(0, 0, 0)",
    position: "relative",
    zIndex: isDragging ? 1 : 0,
    touchAction: isDragging ? "none" : "auto", // Prevents touch scrolling while dragging
  } as const;

  return (
    <BaseCard
      ref={setNodeRef}
      style={style}
      className={`bg-white hover:bg-gray-50 no-select ${isDragging ? "opacity-50 shadow-lg" : ""}`}
    >
      <div className="flex items-center">
        <div
          className="cursor-grab active:cursor-grabbing py-4 px-4"
          {...attributes}
          {...listeners}
          style={{
            touchAction: "none",
          }}
        >
          <GripVertical className="h-5 w-5 text-gray-400" />
        </div>
        <div className={clsx("flex flex-col flex-grow py-4", isPendingChanges && "pr-4")} onClick={() => !isPendingChanges && navigate({ to: `/edit-card/${index}` })}>
          <span className="text-sm font-medium uppercase">{card.type}</span>
          <span className="text-xs text-gray-500">{card.type === 'footer1' ? 'Footer' : card.title}</span>
        </div>
        {!isPendingChanges ? (
          <Link
            to={`/edit-card/$cardIndex`}
            params={{ cardIndex: index.toString() }}
            aria-label="Edit Card"
            className="ml-auto text-blue-500 py-4 px-4"
          >
            <EditIcon className="h-5 w-5" />
          </Link>
        ) : null}
      </div>
    </BaseCard>
  );
}

interface ManageProps {
  cards: CardType[];
  onReorder: (newOrder: CardType[]) => void;
  isPendingChanges: boolean;
}

export function ManageCards({
  cards,
  onReorder,
  isPendingChanges,
}: ManageProps) {
  const [items, setItems] = useState(cards);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setItems((items) => {
      const oldIndex = items.findIndex((_, i) => i === active.id);
      const newIndex = items.findIndex((_, i) => i === over.id);
      const newOrder = arrayMove(items, oldIndex, newIndex);
      onReorder(newOrder);
      return newOrder;
    });
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      autoScroll={true}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext
        items={items.map((_, index) => index)}
        strategy={verticalListSortingStrategy}
      >
        <div className="grid grid-cols-1 gap-4 max-w-md mx-auto">
          {items.map((card, index) => (
            <SortableCard
              key={index}
              index={index}
              card={card}
              isPendingChanges={isPendingChanges}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
